import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';

interface EditarPeriodoDialogProps {
  open: boolean;
  onClose: () => void;
  onGuardar: (diaInicio: number, diaFin: number, mesesOffset: number) => void;
  tipoPeriodo: 'HOME' | 'CALL_CENTER';
  fechaInicioActual: string;
  fechaFinActual: string;
}

export default function EditarPeriodoDialog({
  open,
  onClose,
  onGuardar,
  tipoPeriodo,
  fechaInicioActual,
  fechaFinActual,
}: EditarPeriodoDialogProps) {
  // Extraer días de las fechas actuales
  const diaInicioActual = new Date(fechaInicioActual).getDate();
  const diaFinActual = new Date(fechaFinActual).getDate();

  // Determinar si cruza meses
  const fechaInicioDate = new Date(fechaInicioActual);
  const fechaFinDate = new Date(fechaFinActual);
  const cruzaMeses = fechaFinDate.getMonth() !== fechaInicioDate.getMonth() ||
                     fechaFinDate.getFullYear() !== fechaInicioDate.getFullYear();

  const [diaInicio, setDiaInicio] = useState(diaInicioActual);
  const [diaFin, setDiaFin] = useState(diaFinActual);
  const [cruzaMes, setCruzaMes] = useState(cruzaMeses);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const diaInicio = new Date(fechaInicioActual).getDate();
    const diaFin = new Date(fechaFinActual).getDate();
    const fechaInicio = new Date(fechaInicioActual);
    const fechaFin = new Date(fechaFinActual);
    const cruzaMeses = fechaFin.getMonth() !== fechaInicio.getMonth() ||
                       fechaFin.getFullYear() !== fechaInicio.getFullYear();

    setDiaInicio(diaInicio);
    setDiaFin(diaFin);
    setCruzaMes(cruzaMeses);
    setError(null);
  }, [open, fechaInicioActual, fechaFinActual]);

  const validarPatron = (diaIni: number, diaFi: number, cruzaMes: boolean): string | null => {
    // Validar que los días estén en rango válido
    if (diaIni < 1 || diaIni > 31) {
      return 'El día de inicio debe estar entre 1 y 31';
    }
    if (diaFi < 1 || diaFi > 31) {
      return 'El día de fin debe estar entre 1 y 31';
    }

    // Calcular duración del periodo (asumiendo mes de 31 días)
    let duracion: number;
    if (cruzaMes) {
      // Si cruza mes: días desde el día inicio hasta fin de mes + días del mes siguiente
      duracion = (31 - diaIni + 1) + diaFi;
    } else {
      // Si no cruza mes: diferencia de días
      if (diaFi <= diaIni) {
        return 'El día de fin debe ser mayor al día de inicio si no cruza mes';
      }
      duracion = diaFi - diaIni + 1;
    }

    if (duracion > 31) {
      return 'El período no puede ser mayor a 31 días';
    }

    return null;
  };

  const handleGuardar = () => {
    const errorValidacion = validarPatron(diaInicio, diaFin, cruzaMes);

    if (errorValidacion) {
      setError(errorValidacion);
      return;
    }

    // Calcular el mesesOffset basado en si cruza mes o no
    const mesesOffset = cruzaMes ? 1 : 0;

    // Pasar directamente los días y el offset, sin generar fechas intermedias
    onGuardar(diaInicio, diaFin, mesesOffset);
    onClose();
  };

  const handleDiaInicioChange = (value: number) => {
    setDiaInicio(value);
    const errorValidacion = validarPatron(value, diaFin, cruzaMes);
    setError(errorValidacion);
  };

  const handleDiaFinChange = (value: number) => {
    setDiaFin(value);
    const errorValidacion = validarPatron(diaInicio, value, cruzaMes);
    setError(errorValidacion);
  };

  const handleCruzaMesChange = (value: boolean) => {
    setCruzaMes(value);
    const errorValidacion = validarPatron(diaInicio, diaFin, value);
    setError(errorValidacion);
  };

  const calcularDias = () => {
    if (cruzaMes) {
      return (31 - diaInicio + 1) + diaFin;
    } else {
      if (diaFin > diaInicio) {
        return diaFin - diaInicio + 1;
      }
    }
    return 0;
  };

  const dias = calcularDias();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Personalizar Patrón de Período {tipoPeriodo === 'HOME' ? 'HOME' : 'CALL CENTER'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              El patrón seleccionado se aplicará a TODOS los periodos de este tipo.
              <br />
              <strong>Ejemplo:</strong> Día 26 al día 25 (del mes siguiente)
              <br />
              <strong>Límite:</strong> Máximo 31 días
              <br />
              <strong>Nota:</strong> Los días se ajustarán automáticamente según el mes (30 días para algunos meses, 28/29 para febrero)
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Día de Inicio</InputLabel>
                <Select
                  value={diaInicio}
                  onChange={(e) => handleDiaInicioChange(Number(e.target.value))}
                  label="Día de Inicio"
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                    <MenuItem key={dia} value={dia}>
                      Día {dia}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Cruza Mes</InputLabel>
                <Select
                  value={cruzaMes ? 'si' : 'no'}
                  onChange={(e) => handleCruzaMesChange(e.target.value === 'si')}
                  label="Cruza Mes"
                >
                  <MenuItem value="no">Mismo Mes</MenuItem>
                  <MenuItem value="si">Mes Siguiente</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Día de Fin</InputLabel>
                <Select
                  value={diaFin}
                  onChange={(e) => handleDiaFinChange(Number(e.target.value))}
                  label="Día de Fin"
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                    <MenuItem key={dia} value={dia}>
                      Día {dia}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="success" sx={{ backgroundColor: '#e8f5e9' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Patrón configurado:
                </Typography>
                <Typography variant="body2">
                  Desde el <strong>día {diaInicio}</strong> hasta el <strong>día {diaFin}</strong>
                  {cruzaMes ? ' del mes siguiente' : ' del mismo mes'}
                </Typography>
              </Alert>
            </Grid>
          </Grid>

          {dias > 0 && (
            <Alert severity={dias <= 31 && !error ? 'success' : 'warning'} sx={{ mt: 2 }}>
              <Typography variant="body2">
                Duración del período: <strong>{dias} día{dias !== 1 ? 's' : ''}</strong>
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Este patrón se aplicará a todos los meses. Por ejemplo:
                <br />
                • Enero: {diaInicio} de enero al {diaFin} de {cruzaMes ? 'febrero' : 'enero'}
                <br />
                • Febrero: {diaInicio} de febrero al {diaFin} de {cruzaMes ? 'marzo' : 'febrero'}
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          variant="contained"
          disabled={!!error}
        >
          Guardar Patrón
        </Button>
      </DialogActions>
    </Dialog>
  );
}
